/** Common contract for everything that produces pitched audio inside a Voice (sample slots, grains, oscillators). */
import type { GestureState } from './types';

export interface SourceUnit {
  readonly output: AudioNode;
  /** Begin sounding at t (nominal hz before the unit's own cents offset). */
  start(t: number, hz: number): void;
  setPitch(hz: number, glideSec: number, t: number): void;
  /** Connect an audio-rate modulator (cents) to every detune param the unit owns now or later. */
  attachDetuneModulator(node: AudioNode): void;
  setGesture?(g: GestureState, t: number): void;
  /** Schedule the end of sound at t (the unit frees itself shortly after). */
  stop(t: number): void;
  dispose(): void;
}

export interface UnitConfig {
  ctx: AudioContext;
  /** fixed offset in cents (layer semitoneOffset, unison detune, sub/fifth/octave interval) */
  cents: number;
  /** linear level */
  level: number;
  /** 0..1 randomised loop-start phase */
  phaseScatter: number;
  seed: number;
}

/** Nothing below this is audible on ordinary speakers; a layer that lands under it is folded up by octaves. */
export const LOWEST_HZ = 36;
export const effectiveHz = (hz: number, cents: number): number => {
  let f = hz * Math.pow(2, cents / 1200);
  if (!(f > 0)) return LOWEST_HZ;
  while (f < LOWEST_HZ) f *= 2;
  return f;
};

export function safeStop(node: AudioScheduledSourceNode, t: number): void {
  try { node.stop(t); } catch { /* not started or already stopped */ }
}

export function disconnectAll(nodes: Iterable<AudioNode>): void {
  for (const n of nodes) { try { n.disconnect(); } catch { /* gone */ } }
}
